import { Link, useSearchParams } from "react-router-dom";
import { userApi } from "../../lib/api";
import { usePatreonLink } from "../../lib/patreon";
import { useUntilFulfilled } from "../hooks/useUntilFulfilled";
import { useSession } from "../../lib/session";
import { GoldChip } from "../ui/Gold";
import { DiscordLink } from "../../components/DiscordLink";
import { PatreonPremium } from "../components/PatreonPremium";

/** How long to keep asking before saying "it's on its way". */
const GIVE_UP_MS = 45_000;
const EVERY_MS = 2_000;

/**
 * Where PayzMoon sends the buyer back to.
 *
 * IT POLLS BECAUSE THE REDIRECT AND THE WEBHOOK ARE A RACE, and the webhook usually loses:
 * the browser comes straight back from a checkout on their domain while the grant is still
 * a server-to-server hop away. Showing "you're Gold" off the redirect alone would be a lie
 * about half the time, and showing nothing would look like the money vanished.
 *
 * TIMING OUT IS NOT FAILING. If the grant has not landed in forty-five seconds the honest
 * message is that it is on its way — a webhook can be retried minutes later, and the
 * entitlement will simply appear. Telling somebody their payment failed when it did not is
 * far worse than telling them to check back.
 *
 * It asks the SERVER rather than trusting the redirect's query string, because everything
 * in that url came back through the buyer's browser and none of it is evidence of payment.
 * The only thing that grants Gold is a signed webhook.
 *
 * PATREON LANDS HERE TOO (`?patreon=`), with the outcome the callback already knows:
 * `active` means the grant landed BEFORE the redirect (the server read the membership
 * in the same request), so the first poll finds it; `taken` and `error` are answers,
 * not waits — the Patreon already bound elsewhere, or a round-trip that did not finish
 * — and each gets its own sentence and no clock.
 *
 * `inactive` — connected, no membership yet — is NEITHER. It is the connect-first flow
 * working: the person did step 1 and is standing at step 2. It used to be a settled
 * dead end that sent them to their profile to "hit Refresh"; now it IS the panel, in
 * its connected-waiting state, so the Subscribe button is right here and the panel's
 * own return-and-poll asks Patreon for them. It must stay out of the PayzMoon branch
 * below: that clock ends in "Payment received.", which nobody here has done yet.
 */
export function GoldThanks() {
  const user = useSession((s) => s.user);
  const refresh = useSession((s) => s.refresh);
  const [params] = useSearchParams();
  const patreon = params.get("patreon");
  const settled = patreon === "taken" || patreon === "error";
  const connectedOnly = patreon === "inactive";
  const link = usePatreonLink();
  const goldUntil = user?.goldUntil ?? 0;
  const isGold = goldUntil > Date.now();

  // Fulfillment is an entitlement on the session, so the ask is just "who am I now" and
  // the answer arrives as isGold flipping — which also switches the clock off.
  const { gaveUp } = useUntilFulfilled({
    enabled: !(isGold || settled || connectedOnly),
    everyMs: EVERY_MS,
    giveUpMs: GIVE_UP_MS,
    check: async (live) => {
      const { user: fresh } = await userApi.me();
      if (live()) refresh(fresh);
    },
  });

  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-16 text-center">
      {settled ? (
        <PatreonOutcome outcome={patreon as "taken" | "error"} />
      ) : isGold ? (
        <>
          <GoldChip />
          <h1 className="mt-4 text-xl font-semibold text-zinc-100">You're Premium.</h1>
          <p className="mt-2 text-sm text-zinc-400">
            Paid through {new Date(goldUntil).toLocaleDateString()}. Thanks for backing this
            thing.
          </p>
        </>
      ) : connectedOnly ? (
        <>
          <h1 className="text-xl font-semibold text-zinc-100">Patreon is connected.</h1>
          <p className="mt-2 text-sm text-zinc-400">
            One step left — subscribe there, and Premium lands here on its own.
          </p>
          {link.state?.enabled ? (
            <div className="mt-6 w-full rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4 text-left">
              <PatreonPremium
                state={link.state}
                busy={link.busy}
                connect={link.connect}
                refresh={link.refresh}
                refreshQuiet={link.refreshQuiet}
                disconnect={link.disconnect}
                source="thanks"
                compact
              />
            </div>
          ) : null}
        </>
      ) : gaveUp ? (
        <>
          <h1 className="text-xl font-semibold text-zinc-100">Payment received.</h1>
          <p className="mt-2 text-sm text-zinc-400">
            Your Premium is taking a moment to land — this happens, and it sorts itself out.
            Check your account in a few minutes; nothing needs doing.
          </p>
        </>
      ) : (
        <>
          <h1 className="text-xl font-semibold text-zinc-100">Unlocking your Premium…</h1>
          <p className="mt-2 text-sm text-zinc-400">
            Waiting on the payment to confirm. This usually takes a few seconds.
          </p>
        </>
      )}
      <Link
        to="/"
        className="mt-6 rounded-lg bg-white/10 px-4 py-2 text-sm text-zinc-200 transition-colors hover:bg-white/20"
      >
        Back to Goon Lords
      </Link>
      {/* Gold only, and only once it has landed: this is the moment somebody has just
          paid to back the site, which is the warmest a visitor ever gets. The waiting
          states are still resolving a payment and get nothing. */}
      {isGold && !settled ? (
        <div className="mt-10 w-full border-t border-white/10 pt-6">
          <p className="text-sm text-zinc-400">The people who did the same are in here.</p>
          <DiscordLink className="mt-3" />
        </div>
      ) : null}
    </div>
  );
}

/** The two Patreon answers that are neither "you're in" nor "one step left". */
function PatreonOutcome({ outcome }: { outcome: "taken" | "error" }) {
  if (outcome === "taken") {
    return (
      <>
        <h1 className="text-xl font-semibold text-zinc-100">That Patreon is already connected.</h1>
        <p className="mt-2 text-sm text-zinc-400">
          It&rsquo;s linked to a different GoonLords account. Disconnect it from that account&rsquo;s
          profile first, or sign in there.
        </p>
      </>
    );
  }
  return (
    <>
      <h1 className="text-xl font-semibold text-zinc-100">Patreon didn&rsquo;t finish.</h1>
      <p className="mt-2 text-sm text-zinc-400">
        The connection didn&rsquo;t complete — the consent page may have been closed, or the link
        expired. Try connecting again from Premium in the menu.
      </p>
    </>
  );
}
